import type { LintFinding } from "../sdk";

// CodeMirror 6 editor for the custom-kernel authoring experience (utility-redesign plan §5).
//
// CodeMirror is the heaviest dependency in the app (plan §9), so everything here is loaded via
// dynamic import — the modules only enter the bundle's lazy chunk and only download when an
// author actually opens the editor (selecting "Custom" / forking a kernel). The main route
// stays light. No CSP change: these are bundled by Vite from node_modules, same-origin.
//
// The determinism linter (src/sdk/lint.ts) is bridged into @codemirror/lint so findings show
// as inline markers on the offending line — the reason CM6 was chosen over a textarea overlay.

export interface CodeEditorHandle {
  /** Current document text. */
  getValue(): string;
  /** Replace the document (e.g. when forking a kernel or restoring a permalink). */
  setValue(text: string): void;
  /** Re-run the linter pass (call after the lint inputs change). */
  refreshLint(): void;
  /** Tear down the editor view. */
  destroy(): void;
}

export interface CodeEditorOptions {
  /** Initial document text. */
  doc: string;
  /** Called (debounced by CM) whenever the document changes. */
  onChange(value: string): void;
  /** The determinism linter — returns findings with optional 1-based line numbers. */
  lint(source: string): LintFinding[];
}

/**
 * Mount a CodeMirror editor into `parent`. Async: dynamically imports CodeMirror so it is
 * code-split out of the main bundle and only fetched on the authoring route.
 */
export async function createCodeEditor(
  parent: HTMLElement,
  options: CodeEditorOptions,
): Promise<CodeEditorHandle> {
  const [
    { EditorView, keymap, lineNumbers, highlightActiveLine },
    { EditorState },
    { javascript },
    lintMod,
    { defaultKeymap, indentWithTab, history, historyKeymap },
    { HighlightStyle, syntaxHighlighting },
    { tags: t },
  ] = await Promise.all([
    import("@codemirror/view"),
    import("@codemirror/state"),
    import("@codemirror/lang-javascript"),
    import("@codemirror/lint"),
    import("@codemirror/commands"),
    import("@codemirror/language"),
    import("@lezer/highlight"),
  ]);
  const { linter, lintGutter } = lintMod;

  // Syntax colours tuned to the app's neutral + teal palette (the editor theme below). Without
  // an explicit HighlightStyle CM6 renders every token in the default ink, so kernel source
  // reads as a flat block — these tags give keywords / strings / numbers / comments colour.
  const highlightStyle = HighlightStyle.define([
    { tag: [t.keyword, t.moduleKeyword, t.controlKeyword, t.operatorKeyword], color: "#0f8f74", fontWeight: "600" },
    { tag: [t.definitionKeyword, t.modifier], color: "#0f8f74", fontWeight: "600" },
    { tag: [t.function(t.variableName), t.function(t.propertyName)], color: "#2d6cdf" },
    { tag: [t.definition(t.variableName), t.definition(t.propertyName)], color: "#1e1f1f" },
    { tag: [t.propertyName], color: "#7a4ec2" },
    { tag: [t.string, t.special(t.string)], color: "#b5651d" },
    { tag: [t.number, t.bool, t.null], color: "#c2410c" },
    { tag: [t.comment, t.lineComment, t.blockComment], color: "rgba(30,31,31,0.45)", fontStyle: "italic" },
    { tag: [t.operator, t.punctuation, t.separator, t.bracket], color: "rgba(30,31,31,0.7)" },
    { tag: [t.typeName, t.className, t.namespace], color: "#0f8f74" },
    { tag: t.invalid, color: "#d11" },
  ]);

  // Map our LintFinding[] onto CM diagnostics. Findings carry a 1-based line; we convert to a
  // document range covering that whole line so the marker sits on the offending line.
  const cmLinter = linter((view) => {
    const text = view.state.doc.toString();
    const findings = options.lint(text);
    return findings.map((f) => {
      const lineNo = f.line && f.line >= 1 && f.line <= view.state.doc.lines ? f.line : 1;
      const line = view.state.doc.line(lineNo);
      return {
        from: line.from,
        to: line.to,
        severity: "error" as const,
        message: f.message,
        source: "determinism linter",
      };
    });
  });

  const state = EditorState.create({
    doc: options.doc,
    extensions: [
      lineNumbers(),
      highlightActiveLine(),
      history(),
      javascript({ typescript: false }),
      syntaxHighlighting(highlightStyle),
      lintGutter(),
      cmLinter,
      keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap]),
      EditorView.theme({
        "&": { fontSize: "13px", backgroundColor: "#fbfbfb", borderRadius: "6px" },
        ".cm-content": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
        "&.cm-focused": { outline: "none" },
        ".cm-gutters": { backgroundColor: "#f3f4f4", border: "none", color: "rgba(30,31,31,0.4)" },
        ".cm-activeLine": { backgroundColor: "rgba(81,192,167,0.06)" },
        ".cm-activeLineGutter": { backgroundColor: "transparent" },
      }),
      EditorView.updateListener.of((u) => {
        if (u.docChanged) options.onChange(u.state.doc.toString());
      }),
    ],
  });

  const view = new EditorView({ state, parent });

  return {
    getValue: () => view.state.doc.toString(),
    setValue: (text: string) => {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
    },
    refreshLint: () => {
      // Nudge the linter by dispatching an empty change set (re-runs the linter source).
      void import("@codemirror/lint").then(({ forceLinting }) => forceLinting(view));
    },
    destroy: () => view.destroy(),
  };
}
